import type { CloudVault, ReadProgress } from '../vault/cloudVault';
import { queueSnapshot } from '../vault/writeQueue';
import { flat, parseArc } from './arc';
import type { Arc } from './arc';
import { buildBoard, isConflictSibling } from './board';
import type { Board } from './board';
import { EMPTY_REGISTRIES, MARKERS_PATH, parseMarkers, parseTags, tagsFromRows, TAGS_PATH } from './registries';
import type { Registries } from './registries';
import { buildFolders, parseTagShards, TAG_SHARDS } from '../entity/folders';
import { homeOf, isOfKind, pathsOfKind } from '../vault/kinds';

export interface Plan {
  board: Board;
  arcs: Arc[];
  registries: Registries;
}

/**
 * Everything the board needs, in as few round trips as the vault allows:
 * one batch for the arcs, one for the two registries — and the catalog, only
 * when a file outside the kind folders has to be weighed.
 */
export async function loadPlan(vault: CloudVault, onProgress?: ReadProgress): Promise<Plan> {
  const home = homeOf('arc');
  const conflictCount = vault.listPaths(home).filter((p) => p.endsWith('.md') && isConflictSibling(p)).length;
  const arcPaths = await pathsOfKind(vault, 'arc');

  const files = await vault.readFiles(arcPaths, onProgress);

  // Pending edits win over what the vault currently holds. Without this a
  // refresh during the debounce would show the pre-edit file and the change
  // would appear to have been dropped.
  const { pending } = queueSnapshot();
  const byPath = new Map(files.map((f) => [f.path, f.text]));
  const candidates = new Set(arcPaths);
  for (const [path, text] of pending) {
    if (candidates.has(path) || path.startsWith(home)) byPath.set(path, text);
  }

  const arcs = [...byPath]
    .map(([path, text]) => parseArc(path, text))
    .filter((arc) => isOfKind('arc', arc.path, flat(arc.frontMatter['kind'])));

  return {
    arcs,
    registries: await loadRegistries(vault),
    board: buildBoard(arcs, conflictCount),
  };
}

/**
 * All of it is optional — a vault without any of it renders untinted chips and
 * an empty folder tree rather than failing.
 *
 * The shards are the tag registry; `_tags.md` is the fallback for a vault no
 * shard write has reached. Only the shards carry `curatedAs`, so folders exist
 * on this head exactly when they do — a `_tags.md`-only vault has tints and no
 * folders, which is the truth rather than a degraded guess.
 */
async function loadRegistries(vault: CloudVault): Promise<Registries> {
  const files = await vault.readFiles([...TAG_SHARDS, TAGS_PATH, MARKERS_PATH]);
  const byPath = new Map(files.map((f) => [f.path, f.text]));
  const rows = parseTagShards(TAG_SHARDS.map((path) => byPath.get(path)));
  const tagsText = byPath.get(TAGS_PATH);
  const markersText = byPath.get(MARKERS_PATH);

  return {
    tags: rows.length > 0 ? tagsFromRows(rows) : tagsText ? parseTags(tagsText) : EMPTY_REGISTRIES.tags,
    markers: markersText ? parseMarkers(markersText) : EMPTY_REGISTRIES.markers,
    folders: buildFolders(rows),
  };
}
